import { useState } from "react";
import {
  approveRequest,
  rejectRequest,
  markAsPrinted,
  markAsDelivered,
} from "@/data/credentialReqRepository";

interface UseRequestListActionsParams {
  userId?: string;
  onSuccess: () => void;
}

export function useRequestListActions({ userId, onSuccess }: UseRequestListActionsParams) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async (requestId: string) => {
    if (!userId) {
      setError("Usuario no autenticado");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await approveRequest(requestId, userId);
      onSuccess();
    } catch (err) {
      console.error("Error approving request:", err);
      setError("Error al aprobar la solicitud");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (requestId: string, notes: string) => {
    if (!userId) {
      setError("Usuario no autenticado");
      return;
    }

    if (!notes.trim()) {
      setError("Las notas de observación son requeridas");
      return;
    }

    if (notes.length > 500) {
      setError("Las notas no pueden exceder 500 caracteres");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await rejectRequest(requestId, userId, notes);
      onSuccess();
    } catch (err) {
      console.error("Error rejecting request:", err);
      setError("Error al observar la solicitud");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkPrinted = async (requestId: string) => {
    if (!userId) {
      setError("Usuario no autenticado");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await markAsPrinted(requestId, userId);
      onSuccess();
    } catch (err) {
      console.error("Error marking as printed:", err);
      setError("Error al marcar como impreso");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkDelivered = async (requestId: string) => {
    if (!userId) {
      setError("Usuario no autenticado");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await markAsDelivered(requestId, userId);
      onSuccess();
    } catch (err) {
      console.error("Error marking as delivered:", err);
      setError("Error al marcar como entregado");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    error,
    clearError: () => setError(null),
    handleApprove,
    handleReject,
    handleMarkPrinted,
    handleMarkDelivered,
  };
}
